import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container
} from '@mui/material';
import { CheckBox, Flag } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import * as meetingService from '../../services/meetingService';
import { normalizeLegacyActions, formatTimestamp } from '../../domain/meetingResult';

const SharedMeetingView = () => {
  const { token } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadSharedMeeting = async () => {
      try {
        const response = await meetingService.getSharedMeeting(token);
        setMeeting(response.data);
        if (response.data?.title) {
          document.title = `${response.data.title} - Shared Meeting`;
        }
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to load shared meeting');
      } finally {
        setLoading(false);
      }
    };

    loadSharedMeeting();
  }, [token]);

  const isStructured = meeting?.is_structured_result === true;
  const analysis = meeting?.analysis;
  const turns = isStructured ? meeting?.transcript_turns || [] : [];
  const legacyActionItems = normalizeLegacyActions(meeting?.actions, meeting?.action_items);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !meeting) {
    return (
      <Container maxWidth="lg">
        <Box mt={4}>
          <Alert severity="error">
            {error || 'Meeting not found or share link has expired'}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mt={4} mb={4}>
        <Box mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            {meeting.title}
          </Typography>
          <Chip label="Shared Meeting" color="primary" size="small" />
        </Box>

        <Grid container spacing={3}>
          {/* Meeting Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Meeting Information
                </Typography>

                {meeting.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {meeting.description}
                    </Typography>
                  </Box>
                )}

                {meeting.recordingType && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Recording Type
                    </Typography>
                    <Chip
                      label={meeting.recordingType === 'live_recording' ? 'Live Recording' : meeting.recordingType === 'bot_recording' ? 'Bot Recording' : 'Upload'}
                      color={meeting.recordingType === 'live_recording' ? 'error' : 'primary'}
                      size="small"
                    />
                  </Box>
                )}

                {meeting.recordingDuration && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Recording Duration
                    </Typography>
                    <Typography variant="body1">
                      {formatTimestamp(meeting.recordingDuration)}
                    </Typography>
                  </Box>
                )}

                {meeting.uploadedBy && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Uploaded By
                    </Typography>
                    <Typography variant="body1">
                      {meeting.uploadedBy.firstName} {meeting.uploadedBy.lastName}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(meeting.createdAt).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {isStructured ? (
            <>
              {/* Safe structured analysis */}
              {(analysis?.summary ||
                (analysis?.decisions || []).length > 0 ||
                (analysis?.next_steps || []).length > 0 ||
                (analysis?.open_questions || []).length > 0 ||
                (analysis?.action_items || []).length > 0) && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Meeting Summary
                      </Typography>
                      {analysis.summary && (
                        <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f9f9f9', mb: 2 }}>
                          <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                            {analysis.summary}
                          </Typography>
                        </Paper>
                      )}
                      {[
                        { label: 'Decisions', items: analysis.decisions || [] },
                        { label: 'Next Steps', items: analysis.next_steps || [] },
                        { label: 'Open Questions', items: analysis.open_questions || [] }
                      ].map((section) =>
                        section.items.length > 0 ? (
                          <Box key={section.label} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              {section.label}
                            </Typography>
                            <List dense disablePadding>
                              {section.items.map((item, index) => (
                                <ListItem key={`${section.label}-${index}`} divider={index < section.items.length - 1} disableGutters>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <Flag fontSize="small" color="action" />
                                  </ListItemIcon>
                                  <ListItemText primary={item} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        ) : null
                      )}
                      {analysis.action_items && analysis.action_items.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Action Items
                          </Typography>
                          <List dense disablePadding>
                            {analysis.action_items.map((item, index) => (
                              <ListItem key={`ai-${index}`} divider={index < analysis.action_items.length - 1} disableGutters>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <CheckBox fontSize="small" color="action" />
                                </ListItemIcon>
                                <ListItemText primary={typeof item === 'string' ? item : item.task} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Display transcript turns (no review flags, no diagnostics) */}
              {turns.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Transcription
                      </Typography>
                      <Box>
                        {turns.map((turn) => (
                          <Box
                            key={turn.id || `${turn.start_seconds}-${(turn.text || '').slice(0, 20)}`}
                            sx={{ display: 'flex', gap: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                          >
                            <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                              {turn.start_seconds != null && (
                                <Typography variant="caption" color="text.secondary">
                                  {formatTimestamp(turn.start_seconds)}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              {turn.speaker_name && (
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {turn.speaker_name}
                                </Typography>
                              )}
                              <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                                {turn.text}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </>
          ) : (
            <>
              {/* Legacy summary */}
              {meeting.summary && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Summary
                      </Typography>
                      <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                        <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                          {meeting.summary}
                        </Typography>
                      </Paper>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Legacy action items (normalized + deduplicated) */}
              {legacyActionItems.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Action Items
                      </Typography>
                      <List>
                        {legacyActionItems.map((item, index) => (
                          <ListItem key={index} divider={index < legacyActionItems.length - 1}>
                            <ListItemIcon>
                              <CheckBox />
                            </ListItemIcon>
                            <ListItemText primary={item} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Legacy transcript */}
              {meeting.transcript && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Transcription
                      </Typography>
                      <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                        <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                          {meeting.transcript}
                        </Typography>
                      </Paper>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Box>
    </Container>
  );
};

export default SharedMeetingView;